import {
  FINANCIAL_PAYLOAD_DECIMAL_PLACES,
  PAYMENT_AMOUNT_DECIMAL_PLACES,
  roundAwayFromZero,
  roundDecimalHalfUp,
  TAX_AGGREGATE_DECIMAL_PLACES,
} from '../utils/decimal-round.util';
import { coerceTradeAdjustmentIsPercent } from '../utils/trade-adjustment.util';
import {
  computeInclusiveLineRawFromUnitGross,
  toInclusiveLinePayloadAmounts,
  calculateDocumentTotalsCore,
  CoreDocumentLineCalculationContext
} from '../utils/inclusive-line-amounts.util';
import { resolvePayloadUnitNet } from '../utils/document-payload-pricing.util';
import { DocumentCreationInfo } from './creation-info.interface';
import {
  DocumentSubType,
  Invoice,
  PaymentType,
  PaymentMethodsList,
  toPosPaymentApiId,
} from './invoice.interface';

export interface DocumentPayload {
  id?: number; // Required for update operations
  documentSubTypeId?: number | string | null;
  note?: string;
  contactId?: string;
  contactName?: string;
  contactPhone?: string;
  driverId?: string;
  salesmanId?: string;
  documentDate: string; // or Date if you're using Date objects
  dueDate?: string; // or Date
  expiryDate?: string; // Required for OfferPrice documents, must be >= documentDate
  branchId?: string | number;
  storeId?: string | number;
  projectId?: string;
  costCenterId?: string;
  paymentType?: number | string;
  currencyId?: string | number;
  IsCommitted?: boolean;
  exchangeRate?: number;
  inventoryCurrencyExchangeRate?: number;
  discountAmount?: number;
  discountIsPercent?: boolean;
  chargeAmount?: number;
  chargeIsPercent?: boolean;
  refreanceInfo?: string;
  pushedAmount?: number;
  basedOnId?: string;
  BasedOnId?: string;
  SessionId?: number;
  sessionId?: number;
  isCommitted?: boolean;
  documentPayments?: DocumentPayment[];
  documentCharges?: DocumentCharge[];
  documentDiscounts?: DocumentDiscount[];
  documentLines: DocumentLine[];
  creationInfo?: DocumentCreationInfo | null;
  createdBy?: string | number | null;
  purchaseDocumentId?: number | string | null;
  salesDocumentId?: number | string | null;
  isHeaderTradeDistributedToLines?: boolean;
  headerTradeDistributionMethod?: string;
  /** Optimistic concurrency token from API GET (required on purchase PUT). */
  rowVersion?: number;
}

/** TAB-3646: unified system currency — omit currency fields; backend assigns company currency. */
export function stripTab3646CurrencyFromDocumentPayload(
  payload: DocumentPayload,
): void {
  delete payload.currencyId;
  delete payload.exchangeRate;
  delete payload.inventoryCurrencyExchangeRate;
}

/** TAB-3646: same for loosely typed payloads (sales order, delivery note, voucher, etc.). */
export function stripTab3646CurrencyFromLoosePayload(
  payload: Record<string, unknown>,
): void {
  delete payload['currencyId'];
  delete payload['CurrencyId'];
  delete payload['exchangeRate'];
  delete payload['ExchangeRate'];
  delete payload['exchangePrice'];
  delete payload['ExchangePrice'];
  delete payload['inventoryCurrencyExchangeRate'];
  delete payload['InventoryCurrencyExchangeRate'];
}

export interface DocumentPayment {
  paymentType: number | string;
  accountId?: number | null;
  amount: number;
  comment?: string;
}

export interface DocumentCharge {
  journalId?: string;
  journalLineId?: string;
  accountId?: string; // Required if journalId is null
  amount?: number; // Required if journalId is null
  comment?: string;
}

export interface DocumentDiscount {
  accountId: string;
  amount: number;
  comment?: string;
}

export interface DocumentLine {
  productId?: number;
  productNameAr: string;
  productNameEn: string;
  productDescription?: string; // Required if productId not provided
  unitId?: string;
  unit?: string | number;
  unitSize?: number;
  quantity: number;
  costAmount?: number;
  price?: number;
  discountAmount?: number;
  discountIsPercent?: boolean;
  chargeAmount?: number;
  chargeIsPercent?: boolean;
  taxAmount?: number;
  taxRate?: number;
  exciseTaxAmount?: number;
  expirationDate?: string; // or Date
  storeId?: string | number;
  basedOnLineId?: number;
  purchaseDocumentLineId?: number;
  salesDocumentLineId?: number;
  projectId?: string;
  costCenterId?: string;
  sessionId?: number;
  documentLineCharges?: DocumentLineCharge[];
  documentLineDiscounts?: DocumentLineDiscount[];
  completeProduct?: any; // Store the complete product object with productUnits
}

export interface DocumentLineCharge {
  journalId?: string;
  journalLineId?: string;
  accountId?: string; // Required if journalId is null
  amount?: number; // Required if journalId is null
  comment?: string;
}

export interface DocumentLineDiscount {
  accountId: string;
  amount: number;
  comment?: string;
}

export interface PaymentTypeOption {
  value?: number | string;
  nameAr?: string;
  nameEn?: string;
}

export function isPaymentTypeOption(value: unknown): value is PaymentTypeOption {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('value' in value || 'nameAr' in value || 'nameEn' in value)
  );
}

/** Payment methods shown in receipt/payment voucher create & edit dropdowns. */
export function isSelectableVoucherPaymentMethod(item: {
  type?: unknown;
  id?: unknown;
}): boolean {
  const type = item?.type;
  const id = Number(item?.id);

  if (
    type === PaymentType.None ||
    type === PaymentType.Debit ||
    type === 'None' ||
    type === 'Debit'
  ) {
    return false;
  }

  if (id === PaymentType.None || id === PaymentType.Debit) {
    return false;
  }

  return true;
}

export function ensureNumericPaymentType(type: any): number | string {
  if (type === null || type === undefined || type === '') {
    return 0;
  }
  if (isPaymentTypeOption(type)) {
    const rawValue = type.value ?? (type as { id?: number | string }).id;
    if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
      return ensureNumericPaymentType(rawValue);
    }
    return 0;
  }
  if (Number.isFinite(Number(type))) {
    return Number(type);
  }
  
  const typeStr = String(type).trim().toLowerCase();
  const found = PaymentMethodsList.find(
    (m) =>
      m.nameEn.toLowerCase() === typeStr ||
      m.nameAr.toLowerCase() === typeStr ||
      m.id.toString() === typeStr
  );
  
  if (found) {
    return found.id;
  }

  // Fallback to enum keys if name matching fails
  if (PaymentType[type as keyof typeof PaymentType] !== undefined) {
    const val = PaymentType[type as keyof typeof PaymentType];
    if (typeof val === 'number') return val;
  }

  return type;
}

/**
 * Determines the payment type based on documentPayments array
 * Returns the single payment type if all payments are the same (Cash, Debit, or Mada)
 * Returns "متعدد" if there are multiple different payment types
 * Returns empty string if no payments exist
 */
function determinePaymentType(documentPayments?: DocumentPayment[]): number | string {
  if (!documentPayments || documentPayments.length === 0) {
    return 0;
  }

  const uniqueTypes = new Set(documentPayments.map((p) => ensureNumericPaymentType(p.paymentType)));

  if (uniqueTypes.size === 1) {
    return Array.from(uniqueTypes)[0];
  }

  // Multiple different payment types
  return PaymentType.MultiPayment;
}

function resolvePriceLevelForPayload(formData: any): 'SalesPrice' | 'PurchasePrice' {
  const dt = formData?.documentSubTypeId ?? formData?.documentType;
  if (dt === DocumentSubType.Purches || dt === DocumentSubType.PurchesReturn) {
    return 'PurchasePrice';
  }
  return 'SalesPrice';
}

function isSalesDocumentForPayload(formData: any): boolean {
  const dt = formData?.documentSubTypeId ?? formData?.documentType;
  return (
    dt === DocumentSubType.Sales ||
    dt === DocumentSubType.SalesReturn ||
    dt === DocumentSubType.OfferPrice
  );
}

/** Sales + sales-return: backend line math from price×rate (needs solve when gross target set). */
function usesSalesBackendLineAmounts(formData: any): boolean {
  const dt = formData?.documentSubTypeId ?? formData?.documentType;
  return (
    dt === DocumentSubType.Sales || dt === DocumentSubType.SalesReturn
  );
}

/** Document currency rounding on persisted net (2 dp). */
function roundDocumentCurrency(value: number): number {
  return roundAwayFromZero(value, PAYMENT_AMOUNT_DECIMAL_PLACES);
}

/** Gross per unit as backend recomputes: price + round(price×rate, 4dp) → 2dp. */
function backendGrossPerUnit(unitNet: number, vatPercent: number): number {
  const rate = vatPercent / 100;
  const taxPerUnit =
    vatPercent > 0
      ? roundAwayFromZero(unitNet * rate, TAX_AGGREGATE_DECIMAL_PLACES)
      : 0;
  return roundDocumentCurrency(unitNet + taxPerUnit);
}

/** Unit net so backend gross per unit equals target (fixes list 10.01). */
function solveUnitNetForBackendGrossPerUnit(
  targetGrossPerUnit: number,
  vatPercent: number,
): number {
  if (vatPercent <= 0) {
    return roundDecimalHalfUp(targetGrossPerUnit, FINANCIAL_PAYLOAD_DECIMAL_PLACES);
  }
  let price = targetGrossPerUnit / (1 + vatPercent / 100);
  for (let i = 0; i < 48; i++) {
    const gross = backendGrossPerUnit(price, vatPercent);
    const err = gross - targetGrossPerUnit;
    if (Math.abs(err) < 1e-9) {
      break;
    }
    price -= err / (1 + vatPercent / 100);
  }
  return roundDecimalHalfUp(price, FINANCIAL_PAYLOAD_DECIMAL_PLACES);
}

/**
 * Sales payload: match DocumentFinancialMath (backend ignores sent taxAmount when taxRate set).
 */
function resolveSalesPayloadUnitAmounts(
  el: any,
  formData: any,
): {
  unitNet: number;
  unitTax: number;
} {
  const qty = Number(el.quantity) || 0;
  const vatPercent = Number(el.vat) || 0;
  if (qty <= 0) {
    return { unitNet: 0, unitTax: 0 };
  }

  const unitGrossTarget = readUnitGrossTargetForPayload(el, formData);

  const unitNet =
    unitGrossTarget != null && vatPercent > 0
      ? solveUnitNetForBackendGrossPerUnit(unitGrossTarget, vatPercent)
      : roundDecimalHalfUp(Number(el.price) || 0, FINANCIAL_PAYLOAD_DECIMAL_PLACES);

  const unitTax =
    vatPercent > 0
      ? roundAwayFromZero(
          unitNet * (vatPercent / 100),
          TAX_AGGREGATE_DECIMAL_PLACES,
        )
      : 0;

  return { unitNet, unitTax };
}

function readInclusiveCatalogUnitPrice(
  line: any,
  priceLevelType: 'SalesPrice' | 'PurchasePrice',
): number | null {
  const product = line.completeProduct;
  if (!product?.productUnits) {
    return null;
  }
  const unit = product.productUnits.find(
    (u: any) => String(u?.unitId) === String(line.unit),
  );
  if (!unit?.withTax) {
    return null;
  }
  const pp = unit.productPrices?.find(
    (p: any) => p?.priceLevel?.type === priceLevelType,
  );
  if (!pp) {
    return null;
  }
  const byUser = Number(pp.priceByUser);
  if (Number.isFinite(byUser) && byUser > 0) {
    return byUser;
  }
  const list = Number(pp.price);
  if (Number.isFinite(list) && list > 0) {
    return list;
  }
  return null;
}

function readRoundedCatalogInclusiveUnitPriceWhenUnchanged(
  line: any,
  formData: any,
): number | null {
  if (!isSalesDocumentForPayload(formData)) {
    return null;
  }

  const vatPercent = Number(line?.vat) || 0;
  if (vatPercent <= 0) {
    return null;
  }

  const inclusiveUnit = readInclusiveCatalogUnitPrice(
    line,
    resolvePriceLevelForPayload(formData),
  );
  if (inclusiveUnit == null) {
    return null;
  }

  const currentRoundedNet = roundDecimalHalfUp(
    Number(line?.price) || 0,
    PAYMENT_AMOUNT_DECIMAL_PLACES,
  );
  const originalRoundedNet = roundDecimalHalfUp(
    inclusiveUnit / (1 + vatPercent / 100),
    PAYMENT_AMOUNT_DECIMAL_PLACES,
  );

  return Math.abs(currentRoundedNet - originalRoundedNet) < 0.0001
    ? inclusiveUnit
    : null;
}

/** Per-unit inclusive gross: user target, else tax-inclusive catalog price. */
function readUnitGrossTargetForPayload(
  el: any,
  formData: any,
): number | null {
  const raw = el.userTargetUnitGross;
  if (
    raw !== null &&
    raw !== undefined &&
    raw !== '' &&
    Number.isFinite(Number(raw)) &&
    Number(raw) > 0
  ) {
    return Number(raw);
  }
  const unchangedRoundedCatalogInclusive =
    readRoundedCatalogInclusiveUnitPriceWhenUnchanged(el, formData);
  if (unchangedRoundedCatalogInclusive != null) {
    return unchangedRoundedCatalogInclusive;
  }
  if (el?.priceEditedManually) {
    return null;
  }
  if (!isSalesDocumentForPayload(formData)) {
    return null;
  }
  return readInclusiveCatalogUnitPrice(
    el,
    resolvePriceLevelForPayload(formData),
  );
}

function isSalesInclusiveUnitLine(el: any, formData: any): boolean {
  if (!isSalesDocumentForPayload(formData)) {
    return false;
  }
  const product = el.completeProduct || el.product;
  if (!product?.productUnits) {
    return false;
  }
  const unit = product.productUnits.find(
    (u: any) => String(u?.unitId) === String(el.unit),
  );
  return !!unit?.withTax;
}

/** Per-unit inclusive gross for payload mapping (never rounds before × qty). */
function resolveUnitGrossForInclusivePayload(
  el: any,
  formData: any,
  vatPercent: number,
): number | null {
  const fromTarget = readUnitGrossTargetForPayload(el, formData);
  if (fromTarget != null && fromTarget > 0) {
    return fromTarget;
  }
  if (!isSalesInclusiveUnitLine(el, formData)) {
    return null;
  }
  const net = Number(el.price) || 0;
  const vat = Number(vatPercent) || 0;
  if (net > 0 && vat > 0) {
    return net * (1 + vat / 100);
  }
  return null;
}

function mapFormDocumentLineToPayloadLine(
  el: any,
  formData: any,
  lineStoreId: number | undefined,
): DocumentLine {
  const dp = FINANCIAL_PAYLOAD_DECIMAL_PLACES;
  const vatPercent = Number(el.vat ?? 0) || 0;
  const taxRate = vatPercent / 100;
  const quantity = Number(el.quantity ?? 0) || 0;
  const discountAmount = resolvePayloadLineDiscountAmount(el, formData);
  const isDiscountPercent = coerceTradeAdjustmentIsPercent(el.discountIsPercent);
  const chargeAmount = resolvePayloadLineChargeAmount(el, formData);
  const isChargePercent = coerceTradeAdjustmentIsPercent(el.chargeIsPercent);

  let unitNet: number;
  let lineTaxAmount: number;

  const inclusiveUnitGross = resolveUnitGrossForInclusivePayload(
    el,
    formData,
    vatPercent,
  );

  if (
    inclusiveUnitGross != null &&
    inclusiveUnitGross > 0 &&
    vatPercent > 0 &&
    quantity > 0 &&
    isSalesDocumentForPayload(formData)
  ) {
    const raw = computeInclusiveLineRawFromUnitGross(
      inclusiveUnitGross,
      quantity,
      vatPercent,
      {
        discountAmount,
        discountIsPercent: isDiscountPercent,
        chargeAmount,
        chargeIsPercent: isChargePercent,
      },
    );
    const payloadAmounts = toInclusiveLinePayloadAmounts(
      raw,
      quantity,
      dp,
      TAX_AGGREGATE_DECIMAL_PLACES,
    );
    unitNet = roundDecimalHalfUp(inclusiveUnitGross / (1 + vatPercent / 100), dp);
    lineTaxAmount = payloadAmounts.taxAmount;
  } else {
    unitNet = resolvePayloadUnitNet(el);
    unitNet = roundDecimalHalfUp(unitNet, dp);

    const lineGross = unitNet * quantity;
    const lineCharge = isChargePercent
      ? (lineGross * chargeAmount) / 100
      : chargeAmount;
    const merchandiseExtended = lineGross + lineCharge;
    const lineDiscount = isDiscountPercent
      ? (merchandiseExtended * discountAmount) / 100
      : discountAmount;

    const adjustedTotal = Math.max(0, merchandiseExtended - lineDiscount);
    lineTaxAmount = roundDecimalHalfUp(adjustedTotal * taxRate, dp);
  }

  const line: DocumentLine = {
    productId: el.productId,
    productNameAr: el.productNameAr,
    productNameEn: el.productNameEn,
    quantity: Number(el.quantity) || 0,
    price: unitNet,
    unitId: el.unit,
    taxAmount: lineTaxAmount,
    taxRate,
    discountAmount,
    discountIsPercent: isDiscountPercent,
    chargeAmount,
    chargeIsPercent: isChargePercent,
    unit: el.unit, // Keep for backward compatibility
    completeProduct: el.completeProduct || el.product, // Preserve the product object
  };

  if (el.cost != null && el.cost !== undefined && el.cost !== '') {
    line.costAmount = el.cost;
  }
  if (el.basedOnLineId != null && el.basedOnLineId !== undefined &&
      (formData.documentSubTypeId === DocumentSubType.SalesReturn ||
       formData.documentSubTypeId === DocumentSubType.PurchesReturn ||
       formData.documentType === DocumentSubType.SalesReturn ||
       formData.documentType === DocumentSubType.PurchesReturn ||
       formData.documentSubTypeId === DocumentSubType.POSSettlement ||
       formData.documentType === DocumentSubType.POSSettlement ||
       formData.documentSubTypeId === 2 || // SalesReturn numeric
       formData.documentSubTypeId === 4 || // PurchesReturn numeric
       formData.documentSubTypeId === 8)) { // POSSettlement numeric (guessing based on common patterns)
    line.basedOnLineId = el.basedOnLineId;
  }
  if (el.purchaseDocumentLineId != null && el.purchaseDocumentLineId !== undefined) {
    line.purchaseDocumentLineId = el.purchaseDocumentLineId;
  }
  if (el.salesDocumentLineId != null && el.salesDocumentLineId !== undefined) {
    line.salesDocumentLineId = el.salesDocumentLineId;
  }
  if (lineStoreId != null && Number.isFinite(lineStoreId)) {
    line.storeId = lineStoreId;
  }
  return line;
}

function toNumericPayloadId(value: unknown): number | string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : String(value);
}

export function mapDocumentPayloadToInvoice(
  payload: DocumentPayload,
  qrCode: string
): Invoice {
  const coreLinesContext: CoreDocumentLineCalculationContext[] = payload.documentLines.map((line, index) => {
    const quantity = line.quantity ?? 1;
    const price = line.price ?? 0;
    const gross = price * quantity;

    const chargeAmt = line.chargeAmount ?? (line as any).ChargeAmount ?? 0;
    const chargeIsPct = line.chargeIsPercent ?? (line as any).ChargeIsPercent ?? false;
    const chargeValue = chargeIsPct ? (gross * chargeAmt) / 100 : chargeAmt;

    const merchandiseExtended = gross + chargeValue;

    const discAmt = line.discountAmount ?? (line as any).DiscountAmount ?? 0;
    const discIsPct = line.discountIsPercent ?? (line as any).DiscountIsPercent ?? false;
    const discValue = discIsPct ? (merchandiseExtended * discAmt) / 100 : discAmt;

    const rawLineNet = Math.max(0, merchandiseExtended - discValue);
    
    let vatPercent = 0;
    if (line.taxRate !== undefined && line.taxRate !== null) {
      vatPercent = line.taxRate * 100;
    } else if (rawLineNet > 0 && line.taxAmount) {
      // Fallback: estimate from taxAmount, but ideally taxAmount here isn't already distributed. 
      // Safest fallback if taxRate is inexplicably missing.
      vatPercent = (line.taxAmount / rawLineNet) * 100;
    }

    return {
      index,
      rawLineNet,
      vatPercent
    };
  });

  const headerDiscountAmount = payload.discountAmount ?? 0;
  const headerDiscountIsPercent = payload.discountIsPercent ?? false;
  const headerChargeAmount = payload.chargeAmount ?? 0;
  const headerChargeIsPercent = payload.chargeIsPercent ?? false;

  const coreTotals = calculateDocumentTotalsCore(
    coreLinesContext,
    headerDiscountAmount,
    headerDiscountIsPercent,
    headerChargeAmount,
    headerChargeIsPercent
  );

  const taxAmount = coreTotals.rawTotalVat;
  const totalAmount = coreTotals.rawTotalNet;

  // Convert basedOnId/BasedOnId to number
  const basedOnIdValue = payload.basedOnId ?? payload.BasedOnId;

  return {
    id: 0, // Default to 0 for new invoices
    documentSubTypeId: (() => {
      const v = payload.documentSubTypeId;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    })(),
    serialNumber: '', // Generated by backend
    note: payload.note ?? '',
    contactId: payload.contactId ? Number(payload.contactId) : 0,
    contact: undefined,
    contactName: payload.contactName ?? '',
    contactPhone: payload.contactPhone ?? '',
    driverId: payload.driverId ? Number(payload.driverId) : 0,
    salesmanId: payload.salesmanId ? Number(payload.salesmanId) : 0,
    documentDate: new Date(payload.documentDate),
    dueDate: payload.dueDate
      ? new Date(payload.dueDate)
      : new Date(payload.documentDate),
    net: taxAmount + totalAmount, // Calculated by backend
    taxAmount,
    branchId: payload.branchId ? Number(payload.branchId) : 0,
    totalAmount: undefined, // Calculated by backend
    branch: undefined,
    projectId: payload.projectId ? Number(payload.projectId) : 0,
    costCenterId: payload.costCenterId ? Number(payload.costCenterId) : 0,
    currencyId: payload.currencyId ? Number(payload.currencyId) : 0,
    exchangeRate: payload.exchangeRate ?? 1,
    discountAmount: payload.discountAmount ?? 0,
    discountIsPercent: payload.discountIsPercent ?? false,
    chargeAmount: payload.chargeAmount ?? 0,
    chargeIsPercent: payload.chargeIsPercent ?? false,
    refreanceInfo: payload.refreanceInfo ?? '',
    paymentType:
      determinePaymentType(payload.documentPayments) ||
      payload.paymentType ||
      '',
    BasedOnId: basedOnIdValue ? Number(basedOnIdValue) : 0,
    pushedAmount: payload.pushedAmount ?? 0,
    basedOnId: basedOnIdValue ? Number(basedOnIdValue) : 0,
    sessionId: payload.SessionId ?? 0,
    isCommitted: payload.isCommitted ?? payload.IsCommitted ?? false,
    documentPayments: payload.documentPayments ?? [],
    documentCharges: [],
    documentDiscounts: [],
    documentLines: payload.documentLines.map((line, i) => {
      const lineTotals = coreTotals.lines[i];
      const lineTotalAmount = lineTotals.rawTaxableAmount;
      const lineTaxAmount = lineTotals.rawLineTax;
      const lineNet = lineTotals.rawLineTotal;

      const chargeAmt = line.chargeAmount ?? (line as any).ChargeAmount ?? 0;
      const chargeIsPct = line.chargeIsPercent ?? (line as any).ChargeIsPercent ?? false;
      const discAmt = line.discountAmount ?? (line as any).DiscountAmount ?? 0;
      const discIsPct = line.discountIsPercent ?? (line as any).DiscountIsPercent ?? false;

      return {
      id: 0,
      // ProductId: line.productId,
      product: line.completeProduct
        ? {
            ...line.completeProduct,
            // Ensure we have the complete product with productUnits
          }
        : {
            nameAr: line.productNameAr,
            nameEn: line.productNameEn,
            code: '',
            productType: 'Sales',
            taxCategoryId: 0,
            initialCost: 0,
            productUnits: [],
          }, // Must be provided in payload
      // productName: line.productName,
      // ProductDescription: line.ProductDescription ?? '',
      UnitId: line.unitId,
      unitId: line.unitId,
      unit: line.unit ?? line.unitId ?? '',
      UnitSize: line.unitSize ?? 0,
      Quantity: line.quantity,
      quantity: line.quantity,
      // availableQuantity: line.availableQuantity ?? 0,
      // CostAmount: line.CostAmount ?? 0,
      Price: line.price,
      productId: line.productId!,
      price: line.price,
      net: lineNet,
      Net: lineNet,
      totalAmount: lineTotalAmount,
      TotalAmount: lineTotalAmount,
      DiscountAmount: discAmt,
      DiscountIsPercent: discIsPct,
      ChargeAmount: chargeAmt,
      ChargeIsPercent: chargeIsPct,
      TaxAmount: lineTaxAmount,
      taxAmount: lineTaxAmount,
      // ExciseTaxAmount: line.ExciseTaxAmount ?? 0,
      // ExpirationDate: line.ExpirationDate ?? '',
      // StoreId: line.StoreId ?? '',
      // ProjectId: line.ProjectId ?? '',
      // CostCenterId: line.CostCenterId ?? '',
      SessionId: line.sessionId,
      DocumentLineCharges: [],
      DocumentLineDiscounts: [],
      };
    }),
    journals: [],
    eInvoiceState: 'Pending', // Default state
    eInvoiceQrCode: qrCode, // From function parameter
    paymentDetails: (payload.documentPayments ?? []).map((payment) => ({
      id: 0,
      documentId: 0,
      paymentType: payment.paymentType,
      accountId:
        payment.accountId != null ? Number(payment.accountId) || 0 : 0,
      glAccount: undefined,
      amount: payment.amount,
      description: payment.comment ?? '',
    })),
  };
}

function resolvePayloadLineDiscountAmount(el: any, formData: any): number {
  return Number(el.discountAmount ?? 0) || 0;
}

function resolvePayloadLineChargeAmount(el: any, formData: any): number {
  return Number(el.chargeAmount ?? 0) || 0;
}

/** Return with source invoice: header trade is already on line distributed amounts. */
function isReturnDocumentWithBasedOnReference(formData: any): boolean {
  const isReturn =
    formData.documentSubTypeId === DocumentSubType.SalesReturn ||
    formData.documentSubTypeId === DocumentSubType.PurchesReturn ||
    formData.documentType === DocumentSubType.SalesReturn ||
    formData.documentType === DocumentSubType.PurchesReturn ||
    formData.documentSubTypeId === 2 ||
    formData.documentSubTypeId === 4;
  const basedOnId = Number(formData.BasedOnId ?? formData.basedOnId ?? 0);
  return isReturn && Number.isFinite(basedOnId) && basedOnId > 0;
}

export function mapInvoiceFormToDocumentPayload(
  formData: any,
  total: number,
  inventoryCurrencyExchangeRate: number = 1,
  contact?: any,
  /** When true (POS), API payload uses isCommitted/IsCommitted false; sales/purchase invoices use true. */
  isPosPayload = false,
): DocumentPayload {
  const resolvedCommitted = !isPosPayload;
  // Get contact name based on current language or default to English
  const getContactName = (): string => {
    if (contact) {
      // Try to get from contact object
      return contact.nameAr || contact.nameEn || '';
    }
    // Fallback to formData if contact object not provided
    if (formData.contact) {
      return formData.contact.nameAr || formData.contact.nameEn || '';
    }
    if (formData.contactName) {
      return formData.contactName;
    }
    return '';
  };

  const getContactPhone = (): string => {
    if (contact) {
      return contact.phone || '';
    }
    if (formData.contact) {
      return formData.contact.phone || '';
    }
    if (formData.contactPhone) {
      return formData.contactPhone;
    }
    return '';
  };

  const lineStoreNumeric = (() => {
    const n = Number(formData.storeId);
    return Number.isFinite(n) ? n : undefined;
  })();

  const validLines = formData.documentLines.filter((el: any) => el.quantity > 0);
  const headerTradeFoldedIntoLines = false;

  const coreLines: CoreDocumentLineCalculationContext[] = validLines.map((el: any, index: number) => {
    const quantity = Number(el.quantity ?? 0) || 0;
    const vatPercent = Number(el.vat ?? 0) || 0;
    const discountAmount = resolvePayloadLineDiscountAmount(el, formData);
    const isDiscountPercent = coerceTradeAdjustmentIsPercent(el.discountIsPercent);
    const chargeAmount = resolvePayloadLineChargeAmount(el, formData);
    const isChargePercent = coerceTradeAdjustmentIsPercent(el.chargeIsPercent);

    let rawLineNet: number;

    const inclusiveUnitGross = resolveUnitGrossForInclusivePayload(el, formData, vatPercent);
    if (inclusiveUnitGross != null && inclusiveUnitGross > 0 && vatPercent > 0 && quantity > 0 && isSalesDocumentForPayload(formData)) {
      const raw = computeInclusiveLineRawFromUnitGross(
        inclusiveUnitGross,
        quantity,
        vatPercent,
        { discountAmount, discountIsPercent: isDiscountPercent, chargeAmount, chargeIsPercent: isChargePercent }
      );
      rawLineNet = raw.rawLineNet;
    } else {
      const dp = FINANCIAL_PAYLOAD_DECIMAL_PLACES;
      let unitNet = resolvePayloadUnitNet(el);
      unitNet = roundDecimalHalfUp(unitNet, dp);
      const lineGross = unitNet * quantity;
      const lineCharge = isChargePercent ? (lineGross * chargeAmount) / 100 : chargeAmount;
      const merchandiseExtended = lineGross + lineCharge;
      const lineDiscount = isDiscountPercent ? (merchandiseExtended * discountAmount) / 100 : discountAmount;
      rawLineNet = Math.max(0, merchandiseExtended - lineDiscount);
    }

    return { index, rawLineNet, vatPercent };
  });

  const headerDiscountAmount = headerTradeFoldedIntoLines
    ? 0
    : Number(formData.discountAmount ?? 0) || 0;
  const headerDiscountIsPercent = coerceTradeAdjustmentIsPercent(
    formData.discountIsPercent,
  );
  const headerChargeAmount = headerTradeFoldedIntoLines
    ? 0
    : Number(formData.chargeAmount ?? 0) || 0;
  const headerChargeIsPercent = coerceTradeAdjustmentIsPercent(
    formData.chargeIsPercent,
  );

  const coreTotals = calculateDocumentTotalsCore(
    coreLines,
    headerDiscountAmount,
    headerDiscountIsPercent,
    headerChargeAmount,
    headerChargeIsPercent
  );

  const mappedDocumentLines = validLines.map((el: any, i: number) => {
    const mappedLine = mapFormDocumentLineToPayloadLine(el, formData, lineStoreNumeric);
    // Override line tax amount with exactly distributed line tax
    mappedLine.taxAmount = roundDecimalHalfUp(coreTotals.lines[i].rawLineTax, FINANCIAL_PAYLOAD_DECIMAL_PLACES);
    return mappedLine;
  });

  const computedDocumentTotal = roundDecimalHalfUp(
    Number(total) || 0,
    PAYMENT_AMOUNT_DECIMAL_PLACES,
  );

  const basePayments: any[] =
    formData.documentPayments && formData.documentPayments.length > 0
      ? [...formData.documentPayments]
      : [
          {
            paymentType: formData.PaymentType,
            accountId: 0,
            amount: computedDocumentTotal,
          },
        ];

  const positiveBefore = basePayments.filter(
    (el: any) => Number(el.amount) > 0,
  );
  const sumBefore = positiveBefore.reduce(
    (s, p: any) => s + (Number(p.amount) || 0),
    0,
  );

  let adjustedPayments = positiveBefore;
  if (positiveBefore.length === 1) {
    adjustedPayments = [
      { ...positiveBefore[0], amount: computedDocumentTotal },
    ];
  } else if (positiveBefore.length > 1 && sumBefore > 0) {
    let running = 0;
    adjustedPayments = positiveBefore.map((p: any, i: number) => {
      const last = i === positiveBefore.length - 1;
      const share = last
        ? roundDecimalHalfUp(
            computedDocumentTotal - running,
            PAYMENT_AMOUNT_DECIMAL_PLACES,
          )
        : roundDecimalHalfUp(
            (Number(p.amount) || 0) * (computedDocumentTotal / sumBefore),
            PAYMENT_AMOUNT_DECIMAL_PLACES,
          );
      if (!last) {
        running += share;
      }
      return { ...p, amount: share };
    });
  } else if (positiveBefore.length > 1 && sumBefore <= 0) {
    adjustedPayments = [
      { ...positiveBefore[0], amount: computedDocumentTotal },
    ];
  } else if (positiveBefore.length === 0 && computedDocumentTotal > 0) {
    adjustedPayments = [
      {
        paymentType: formData.PaymentType,
        accountId: 0,
        amount: computedDocumentTotal,
      },
    ];
  }

  const filteredDocumentPayments = adjustedPayments.filter(
    (el: any) => Number(el.amount) > 0,
  );

  const mapPaymentTypeForPayload = (type: unknown): number | string =>
    isPosPayload ? toPosPaymentApiId(type as any) : ensureNumericPaymentType(type);

  // Determine payment type based on filtered documentPayments
  const determinePaymentType = (): number | string => {
    if (filteredDocumentPayments.length === 0) {
      return mapPaymentTypeForPayload(
        formData.PaymentType || PaymentType.MultiPayment,
      );
    }

    // Get unique payment types from filtered payments
    const uniquePaymentTypes = new Set(
      filteredDocumentPayments.map((p: any) =>
        mapPaymentTypeForPayload(p.paymentType),
      ),
    );

    // If there's only one unique payment type, use it
    if (uniquePaymentTypes.size === 1) {
      return Array.from(uniquePaymentTypes)[0];
    }

    // If there are multiple different payment types, use MultiPayment
    return PaymentType.MultiPayment;
  };

  return {
    expiryDate: formData.expiryDate,
    contactId: formData.contactId,
    contactName: getContactName(),
    contactPhone: getContactPhone(),
    documentDate: new Date(formData.documentDate).toISOString().split('T')[0],
    dueDate: new Date(formData.documentDate).toISOString().split('T')[0],
    // API expects numeric subtype id; form may hold enum string (e.g. "Sales") for UI only — do not send that.
    documentSubTypeId: 0,
    note: formData.note,
    paymentType: determinePaymentType(),
    BasedOnId:
      (formData.documentSubTypeId === DocumentSubType.SalesReturn ||
       formData.documentSubTypeId === DocumentSubType.PurchesReturn ||
       formData.documentType === DocumentSubType.SalesReturn ||
       formData.documentType === DocumentSubType.PurchesReturn ||
       formData.documentSubTypeId === DocumentSubType.POSSettlement ||
       formData.documentType === DocumentSubType.POSSettlement ||
       formData.documentSubTypeId === 2 ||
       formData.documentSubTypeId === 4 ||
       formData.documentSubTypeId === 8)
        ? formData.BasedOnId : 0,
    basedOnId:
      (formData.documentSubTypeId === DocumentSubType.SalesReturn ||
       formData.documentSubTypeId === DocumentSubType.PurchesReturn ||
       formData.documentType === DocumentSubType.SalesReturn ||
       formData.documentType === DocumentSubType.PurchesReturn ||
       formData.documentSubTypeId === DocumentSubType.POSSettlement ||
       formData.documentType === DocumentSubType.POSSettlement ||
       formData.documentSubTypeId === 2 ||
       formData.documentSubTypeId === 4 ||
       formData.documentSubTypeId === 8)
        ? formData.BasedOnId : 0,
    SessionId: formData.SessionId ?? formData.sessionId,
    sessionId: formData.SessionId ?? formData.sessionId,
    IsCommitted: resolvedCommitted,
    isCommitted: resolvedCommitted,
    branchId: toNumericPayloadId(formData.branchId),
    storeId: toNumericPayloadId(formData.storeId),
    currencyId: toNumericPayloadId(formData.currencyId),
    exchangeRate: formData.exchangeRate ?? 1,
    inventoryCurrencyExchangeRate: inventoryCurrencyExchangeRate,
    purchaseDocumentId:
      (formData.documentSubTypeId === DocumentSubType.Purches ||
        formData.documentType === DocumentSubType.Purches) &&
      formData.RefreanceId
        ? toNumericPayloadId(formData.RefreanceId)
        : undefined,
    salesDocumentId:
      (formData.documentSubTypeId === DocumentSubType.Sales ||
        formData.documentType === DocumentSubType.Sales) &&
      formData.RefreanceId
        ? toNumericPayloadId(formData.RefreanceId)
        : undefined,
    documentLines: mappedDocumentLines,
    discountAmount: headerTradeFoldedIntoLines
      ? 0
      : Number(formData.discountAmount ?? 0) || 0,
    discountIsPercent: headerDiscountIsPercent,
    chargeAmount: headerTradeFoldedIntoLines
      ? 0
      : Number(formData.chargeAmount ?? 0) || 0,
    chargeIsPercent: headerChargeIsPercent,
    documentPayments: filteredDocumentPayments.map((p: any) => ({
      ...p,
      paymentType: mapPaymentTypeForPayload(p.paymentType),
      accountId: p.accountId ?? null,
      amount: roundDecimalHalfUp(
        Number(p.amount) || 0,
        PAYMENT_AMOUNT_DECIMAL_PLACES,
      ),
    })),
    // isHeaderTradeDistributedToLines: true,
    // headerTradeDistributionMethod: "Manual",
  };
}
